import React, { useEffect, useRef, useState } from "react";
import Matter from "matter-js";
import styles from "src/components/dashboard/Dashboard.module.css";

const badgesInfo = [
  {
    w: 390,
    h: 390,
    name: "AWS",
    desiredH: 150,
    lines: [
      { x: 195, y: 5 },
      { x: 360, y: 100 },
      { x: 360, y: 290 },
      { x: 195, y: 385 },
      { x: 30, y: 290 },
      { x: 30, y: 100 },
    ],
  },
  {
    w: 150,
    h: 215,
    name: "CSA",
    desiredH: 150,
    lines: [
      { x: 75, y: -3 },
      { x: 150, y: 44 },
      { x: 150, y: 170 },
      { x: 75, y: 216 },
      { x: 0, y: 170 },
      { x: 0, y: 44 },
    ],
  },
  {
    w: 584,
    h: 170,
    name: "uc_logo_inline",
    desiredH: 40,
    color: "#ffffff",
  },
];

function BadgesWidget({ containerRef }) {
  const sceneRef = useRef(null);
  const renderRef = useRef(null);
  const engineRef = useRef(Matter.Engine.create());
  const [bodies, setBodies] = useState([]);
  const [containerSize, setContainerSize] = useState(null);
  const spawnedBodiesRef = useRef([]);

  const playWidth = 300;
  const playHeight = 400;
  const wallThickness = 400;

  const debugDrawMatter = false;

  const wallRefs = useRef(null);

  let wallOptions = { isStatic: true };

  useEffect(() => {
    const engine = engineRef.current;
    const world = engine.world;
    const badgeBodies = [];

    let initialWidth = containerSize?.width ?? playWidth;
    let initialHeight = containerSize?.height ?? playHeight;

    //load the badges!!
    badgesInfo.forEach((bi, i) => {
      setTimeout(() => {
        if (bi.lines != null) {
          //Assume its a complex polygon shape
          let lines = bi.lines;
          if (bi.desiredH) {
            let ratio = bi.desiredH / bi.h;
            lines = bi.lines.map((line) => {
              return { x: line.x * ratio, y: line.y * ratio };
            });
          }

          const badgeBody = Matter.Bodies.fromVertices(
            initialWidth / 2,
            -bi.h,
            lines,
            {
              restitution: 0.8,
              frictionAir: 0.007,
            },
            true
          );
          Matter.World.add(world, badgeBody);
          badgeBodies.push({ badgeBody, badgeInfo: bi });
        } else if (bi.radius != null) {
          //Assume its a circle image
          let radius = bi.radius;
          if (bi.desiredH) {
            radius = bi.desiredH / 2;
          }
          const badgeBody = Matter.Bodies.circle(
            initialWidth / 2,
            -bi.h,
            radius,
            {
              restitution: 0.8,
              //frictionAir: 0.1, default is 0.01
            },
            true
          );
          Matter.World.add(world, badgeBody);
          badgeBodies.push({ badgeBody, badgeInfo: bi });
        } else {
          //assume its a rectangle image now.
          let w = bi.w;
          let h = bi.h;
          if (bi.desiredH !== null) {
            let ratio = bi.desiredH / bi.h;
            w = w * ratio;
            h = h * ratio;
          }
          const badgeBody = Matter.Bodies.rectangle(
            initialWidth / 2,
            -bi.h,
            w,
            h,
            {
              restitution: 0.8,
              frictionAir: 0.007,
            }
          );
          Matter.World.add(world, badgeBody);
          badgeBodies.push({ badgeBody, badgeInfo: bi });
        }
      }, (i + 1) * 1000); //this is so they spawn in 1 second after another
    });

    let walls = [
      // Top
      Matter.Bodies.rectangle(
        initialWidth / 2,
        -wallThickness / 2 - 800,
        initialWidth,
        wallThickness,
        wallOptions
      ),
      // Bottom
      Matter.Bodies.rectangle(
        initialWidth / 2,
        initialHeight + wallThickness / 2,
        initialWidth,
        wallThickness,
        wallOptions
      ),
      // Left
      Matter.Bodies.rectangle(
        -wallThickness / 2,
        initialHeight / 2,
        wallThickness,
        initialHeight,
        wallOptions
      ),
      // Right
      Matter.Bodies.rectangle(
        initialWidth + wallThickness / 2,
        initialHeight / 2,
        wallThickness,
        initialHeight,
        wallOptions
      ),
    ];

    wallRefs.current = walls;
    Matter.World.add(engine.world, [...walls]);

    // Mouse interaction
    const mouse = Matter.Mouse.create(sceneRef.current);
    const mouseConstraint = Matter.MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false },
      },
    });

    Matter.World.add(engine.world, mouseConstraint);

    // Keep mouse in sync with the scene
    mouse.pixelRatio = window.devicePixelRatio;

    const handleAfterUpdate = () => {
      const world = engine.world;

      world.bodies.forEach((body) => {
        if (body.position.y > 5000) {
          let width =
            containerRef.current?.getBoundingClientRect()?.width ?? 20;
          let height =
            containerRef.current?.getBoundingClientRect()?.height ?? 20;
          // Respawn it back to a defined location
          Matter.Body.setPosition(body, { x: width / 2, y: -50 });

          // Reset velocity so it doesn't fall again immediately
          Matter.Body.setVelocity(body, { x: 0, y: 0 });

          // Optionally reset rotation
          Matter.Body.setAngle(body, 0);
          Matter.Body.setAngularVelocity(body, 0);
        }
      });
    };
    Matter.Events.on(engine, "afterUpdate", handleAfterUpdate);

    // Create render object to display hitboxes (wireframe mode)

    let render;
    if (debugDrawMatter) {
      render = Matter.Render.create({
        element: sceneRef.current, // Attach to the sceneRef div
        engine: engine,
        options: {
          width: playWidth,
          height: playHeight,
          wireframes: true, // This draws the wireframe hitboxes
          background: "transparent",
        },
      });
      Matter.Render.run(render);
      renderRef.current = render;
    }
    // Run the engine and the render
    // Matter.Engine.run(engine);

    const FIXED_TIME_STEP = 1000 / 60; // 16.666ms
    let lastTime = performance.now();
    let isRunning = true;
    let animationFrameId;

    function update(currentTime) {
      if (!isRunning) {
        animationFrameId = requestAnimationFrame(update); // keep the loop alive
        return;
      }
      let delta = currentTime - lastTime;

      // Cap delta to prevent big jumps on resume
      delta = Math.min(delta, 100); // don't process more than ~6 frames worth

      // Run fixed timestep updates (can run multiple steps per frame if needed)
      while (delta >= FIXED_TIME_STEP) {
        Matter.Engine.update(engine, FIXED_TIME_STEP);
        delta -= FIXED_TIME_STEP;
        lastTime += FIXED_TIME_STEP;
      }

      // spawnedBodiesRef.current.forEach((body, i) => {
      //   const el = document.getElementById(`box-${i}`);
      //   if (el) {
      //     el.style.transform = `translate(${body.position.x - 30}px, ${
      //       body.position.y - 30
      //     }px) rotate(${body.angle}rad)`;
      //   }
      // });

      for (let bb of badgeBodies) {
        const el = document.getElementById(`custom-${bb.badgeInfo.name}`);
        if (el) {
          let width = bb.badgeInfo.w;
          let height = bb.badgeInfo.h;
          if (bb.badgeInfo.desiredH) {
            if (bb.badgeInfo.radius) {
              width = bb.badgeInfo.desiredH / 2;
              height = bb.badgeInfo.desiredH / 2;
            }
            let ratio = bb.badgeInfo.desiredH / bb.badgeInfo.h;
            width = bb.badgeInfo.w * ratio;
            height = bb.badgeInfo.h * ratio;
          }
          el.style.width = `${width}px`;
          el.style.height = `${height}px`;
          el.style.transform = `
            translate(${bb.badgeBody.position.x - width / 2}px, ${
            bb.badgeBody.position.y - height / 2
          }px)
            rotate(${bb.badgeBody.angle}rad)
          `;
          el.style.display = "inline-block";
        }
      }

      animationFrameId = requestAnimationFrame(update);
    }
    // Handle tab visibility
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isRunning = false; // Pause the simulation
      } else {
        isRunning = true;
        lastTime = performance.now(); // Reset time to avoid jump
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    animationFrameId = requestAnimationFrame(update);

    return () => {
      if (debugDrawMatter) {
        Matter.Render.stop(render);
      }
      cancelAnimationFrame(animationFrameId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      Matter.Events.off(engine, "afterUpdate", handleAfterUpdate);
      Matter.Engine.clear(engine);
      Matter.World.clear(engine.world);
    };
  }, []);

  // Observe size changes of the container
  useEffect(() => {
    if (!containerRef?.current || !wallRefs?.current || !engineRef) return;

    const updateSize = () => {
      let width = containerRef.current?.getBoundingClientRect()?.width;
      let height = containerRef.current?.getBoundingClientRect()?.height;
      setContainerSize({
        width: width,
        height: height,
      });
      if (!width) return;

      if (debugDrawMatter) {
        renderRef.current.canvas.width = width;
        renderRef.current.canvas.height = height;
      }

      wallRefs.current.forEach((wall) =>
        Matter.World.remove(engineRef.current.world, wall)
      );

      let walls = [
        // Top
        Matter.Bodies.rectangle(
          width / 2,
          -wallThickness / 2 - 800,
          width + 5000,
          wallThickness,
          wallOptions
        ),
        // Bottom
        Matter.Bodies.rectangle(
          width / 2,
          height + wallThickness / 2,
          width + 5000,
          wallThickness,
          wallOptions
        ),
        // Left
        Matter.Bodies.rectangle(
          -wallThickness / 2,
          height / 2,
          wallThickness,
          height + 5000,
          wallOptions
        ),
        // Right
        Matter.Bodies.rectangle(
          width + wallThickness / 2,
          height / 2,
          wallThickness,
          height + 5000,
          wallOptions
        ),
      ];

      wallRefs.current = walls;
      Matter.World.add(engineRef.current.world, [...walls]);
    };

    // Observe size changes
    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    resizeObserver.observe(containerRef.current);

    // Set initial size
    updateSize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return (
    <>
      {/* <button className="bg-white" onClick={() => {
      engineRef.current.world.bodies.forEach(body => {console.log(body);});
    }}>CHECK</button> */}
      <div
        ref={sceneRef}
        style={{
          width: `${containerSize?.width ?? playWidth}px`,
          height: `${containerSize?.height ?? playHeight}px`,
          // border: "1px solid #ccc",
          position: "relative",
          overflow: "hidden",
          // background: "#f0f0f0",
        }}
      >
        {badgesInfo.map((bi) => (
          <div
            id={`custom-${bi.name}`}
            key={`customb-${bi.name}`}
            className={`${styles.draggable} rounded-md box-border`}
            style={{
              position: "absolute",
              // display: "none",
              backgroundColor: bi.color !== null ? bi.color : "",


              // pointerEvents: "auto",
            }}
          >
            <img
              alt=""
              src={`badges/${bi.name}.png`}
              className="w-full h-full object-cover select-none"
              draggable="false"

              style={{
                position: "absolute",
                transformOrigin: "center center",

                // pointerEvents: "none",
              }} />
          </div>
        ))}
      </div>
    </>
  );
}

export default BadgesWidget;
